import { useEffect, useState } from 'react'
import {
  Stack,
  Accordion,
  AccordionSummary,
  Typography,
  TextField,
  useTheme,
  IconButton,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import MailOutlinedIcon from '@mui/icons-material/MailOutlined'
import { useTranslation } from 'react-i18next'
import { fsColors } from '../../../shared/constants'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { validateEmail } from '../../../shared/utils'
import { updateProfile } from '../../../store/actions'
import { Profile } from '../../../shared/model/models'
import { useDebounce } from '../../../shared/hooks'

const FsContactCard = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { account } = useAppSelector((state) => state.currentUser)
  const dispatch = useAppDispatch()
  const [isSaving, setIsSaving] = useState<boolean | null>(null)
  const [uiProfile, setUIProfile] = useState<Profile | null>(null)

  useEffect(() => {
    if (account?.profile) setUIProfile(account.profile)
  }, [account?.profile])

  const debouncedUpdateProfileBE = useDebounce(async () => {
    if (account && uiProfile) {
      try {
        setIsSaving(true)
        await dispatch(updateProfile({ profile: uiProfile }))
      } finally {
        setIsSaving(false)
      }
    }
  })

  return (
    account && (
      <Stack width="100%">
        <Accordion
          sx={{
            backgroundColor: theme.palette.mode === 'dark' ? '' : fsColors.fsDarkBlue.colors[3],
            border: theme.palette.mode === 'dark' ? 1 : '',
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1-content"
            id="panel1-header"
            sx={{ alignItems: 'center' }}
          >
            <Typography>{t('customer.info')}</Typography>
            <Typography variant="caption" ml={2} mt={0.5}>
              {isSaving !== null ? (isSaving ? t('saving.changes') : t('changes.saved')) : ''}
            </Typography>
          </AccordionSummary>
          <Stack
            sx={{
              gap: 2,
              padding: 2,
              backgroundColor: theme.palette.mode === 'dark' ? '' : fsColors.fsDarkBlue.colors[3],
            }}
          >
            <TextField
              label={t('contact.name')}
              value={uiProfile?.contactName ?? ''}
              fullWidth
              sx={{
                backgroundColor: theme.palette.background.paper,
              }}
              onChange={(e) => {
                setUIProfile({
                  ...uiProfile,
                  contactName: e.target.value,
                })
                debouncedUpdateProfileBE()
              }}
            />
            <TextField
              label={t('contact.designation')}
              size="small"
              value={uiProfile?.contactDesignation ?? ''}
              fullWidth
              sx={{
                backgroundColor: theme.palette.background.paper,
              }}
              onChange={(e) => {
                setUIProfile({
                  ...uiProfile,
                  contactDesignation: e.target.value,
                })
                debouncedUpdateProfileBE()
              }}
            />
            <Stack direction="row" gap={2} alignItems="center">
              <TextField
                size="small"
                label={t('email.to')}
                value={uiProfile?.contactEmail ?? ''}
                fullWidth
                sx={{
                  backgroundColor: theme.palette.background.paper,
                }}
                error={!!uiProfile?.contactEmail && !validateEmail(uiProfile?.contactEmail)}
                onChange={(e) => {
                  setUIProfile({
                    ...uiProfile,
                    contactEmail: e.target.value,
                  })
                  if (validateEmail(e.target.value)) debouncedUpdateProfileBE()
                }}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      disabled={!validateEmail(account.profile.contactEmail ?? '')}
                      onClick={() => {
                        window.location.href = `mailto:${account.profile.contactEmail}`
                      }}
                    >
                      <MailOutlinedIcon />
                    </IconButton>
                  ),
                }}
              />
              <TextField
                size="small"
                label={t('phone.number')}
                value={uiProfile?.contactNumber ?? ''}
                fullWidth
                inputMode="numeric"
                sx={{
                  backgroundColor: theme.palette.background.paper,
                }}
                onChange={(e) => {
                  setUIProfile({
                    ...uiProfile,
                    contactNumber: e.target.value,
                  })
                  debouncedUpdateProfileBE()
                }}
              />
            </Stack>
          </Stack>
        </Accordion>
      </Stack>
    )
  )
}

export default FsContactCard
